import type { FormInstance, InputRef, TableColumnsType } from 'antd'
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Dropdown,
  Form,
  Input,
  message,
  Modal,
  notification,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip
} from 'antd'
import type { DefaultOptionType } from 'antd/es/select'
import type { RenderExpandIconProps } from 'rc-table/lib/interface'
import type { Key } from 'react'
import { useCallback, useMemo, useReducer, useRef, useState } from 'react'
import type { ZodIssue } from 'zod'

import {
  CaretDownOutlined,
  CaretRightOutlined,
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  PlusCircleOutlined,
  PlusOutlined,
  SearchOutlined,
  UserAddOutlined
} from '@ant-design/icons'
import { useMutation, useQuery } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import cx from 'classnames'
import { format } from 'date-fns'
import { useDebounce } from 'react-use'

import $api from '../../API'
import { mapper, NotificationEventType } from '../../domain/notification-event-type.enum'

interface Entry {
  id: string

  user: User
  event_types: NotificationEventType[]

  updated_by: User | null
  updated_at: string
}

interface User {
  id: string

  email: string
  first_name: string
  last_name: string

  locations: Location[]
  groups: Group[]

  created_at: string
  updated_at: string
}

interface Location {
  id: string

  key: string
  name: string
  short_name: string
  location: string

  created_at: string
  updated_at: string
}

interface Group {
  id: string

  name: string
  number: string
  location_id: string

  created_at: string
  updated_at: string
}

type QueryContextKey = ['notification_settings', State['search'], State['filters'], State['sort']]

interface MutationPayload {
  user_id: string
  event_types: NotificationEventType[]
}

type MutationError = ValidationError | Error

class ValidationError extends Error {
  issues: Record<string, string>

  constructor(issues: ZodIssue[]) {
    super('Validation error')
    this.issues = Object.fromEntries(issues.map(issue => [issue.path.join('.'), issue.message]))
  }
}

// let roleColorMapper: Record<string, string> = {
//   administrator: 'magenta',
//   account_executive: 'red',
//   account_manager: 'green',
//   call_center: 'purple',
//   check_run: 'orange',
//   check_run_manager: 'volcano',
//   claims: 'lime',
//   finance_manager: 'blue',
// }

const locationColorMapper: Record<string, string> = {
  '003': 'blue',
  '001': 'red',
  '002': 'green',
  '004': 'purple'
}

interface Option extends DefaultOptionType {
  value: string
}

interface FormValues {
  user?: Option
  event_types?: NotificationEventType[]
}

type SortField = 'user' | 'updated_by' | 'updated_at'
type SortOrder = 'asc' | 'desc'

interface State {
  expandedRowId?: string

  search: string

  filters: {
    event_types: NotificationEventType[]
  }

  sort: {
    field?: SortField
    order?: SortOrder
  }
}

type Action =
  | { type: 'row/expand'; payload: string }
  | { type: 'row/collapse' }
  | { type: 'search/set'; payload: string }
  | { type: 'filters/set'; payload: Partial<State['filters']> }
  | { type: 'sort/set'; payload: State['sort'] }

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'row/expand': {
      return {
        ...state,
        expandedRowId: action.payload
      }
    }

    case 'row/collapse': {
      return {
        ...state,
        expandedRowId: undefined
      }
    }

    case 'search/set': {
      return {
        ...state,
        search: action.payload
      }
    }

    case 'filters/set': {
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.payload
        }
      }
    }

    case 'sort/set': {
      return {
        ...state,
        sort: action.payload
      }
    }

    default: {
      return state
    }
  }
}

export function NotificationSettingsPage(): JSX.Element {
  const [form] = Form.useForm<FormValues>()
  const { resetFields, setFieldsValue } = form

  const searchInputRef = useRef<InputRef>(null)

  const [state, dispatch] = useReducer(reducer, {
    search: '',
    filters: {
      event_types: []
    },
    sort: {
      field: 'updated_at',
      order: 'desc'
    }
  })

  const [debouncedSearch, setDebouncedSearch] = useState(state.search)
  const [debouncedLoadingStatus, setDebouncedLoadingStatus] = useState(false)

  useDebounce(
    () => {
      setDebouncedSearch(state.search.toLowerCase().trim())
    },
    500,
    [state.search]
  )

  const query = useQuery<unknown, unknown, Entry[], QueryContextKey>({
    queryKey: ['notification_settings', debouncedSearch, state.filters, state.sort],

    async queryFn(context) {
      const [, search, filters, sort] = context.queryKey
      const searchParams = new URLSearchParams([
        ...filters.event_types.map(key => ['event_types', key]),
        ...(search.length > 0 ? [['search', search]] : []),
        ...(typeof sort.field === 'string' ? [['order-by', sort.field]] : []),
        ...(typeof sort.order === 'string' ? [['direction', sort.order]] : [])
      ])

      const { data } = await $api.get<Entry[]>(
        `/notification-recipients?${searchParams.toString()}`
      )

      return data
    },

    keepPreviousData: true,
    refetchOnMount: true,
    refetchOnWindowFocus: false
  })

  useDebounce(
    () => {
      setDebouncedLoadingStatus(query.isFetching)
    },
    query.isFetching ? 500 : 0,
    [query.isFetching]
  )

  const mutation = useMutation<unknown, MutationError, MutationPayload>({
    async mutationFn(data: MutationPayload) {
      // let isNew = typeof id === 'undefined'

      const url = '/notification-recipients'

      try {
        await $api.post(url, data, {
          headers: {
            'Content-Type': 'application/json'
          }
        })
        dispatch({ type: 'row/collapse' })
        void query.refetch()
      } catch (e) {
        if (e instanceof AxiosError) {
          if (e.status === 422) {
            const data = e.response?.data

            throw new ValidationError(data.issues)
          }

          throw new Error('Failed to update notification settings')
        }
      }
    }
  })

  const locationsQuery = useQuery<unknown, unknown, Location[]>({
    queryKey: ['locations'],

    async queryFn() {
      const response = await $api.get('/locations')

      return response.data
    }
  })

  const handleExpand = useCallback(
    (expanded: boolean, entry: Entry) => {
      resetFields()

      if (!expanded) {
        dispatch({ type: 'row/collapse' })
        return
      }

      dispatch({ type: 'row/expand', payload: entry.id })

      setFieldsValue({
        user: {
          value: entry.user.id,
          label: <UserLabel user={entry.user} />
        },
        event_types: entry.event_types
      })
    },
    [resetFields, setFieldsValue]
  )

  const handleDelete = useCallback(
    (id: string) => {
      const url = '/notification-recipients'

      $api
        .post(
          url,
          {
            user_id: id,
            event_types: []
          },
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        )
        .then(async () => {
          dispatch({ type: 'row/collapse' })
          return query.refetch()
        })
        .catch((err: unknown) => {
          notification.error({
            message: 'Error',
            description: err instanceof Error ? err.message : 'Unknown error'
          })
          throw new Error('Failed to update notification settings')
        })
    },
    [query]
  )

  const columns = useMemo<TableColumnsType<Entry>>(
    () => [
      {
        title: 'User',
        dataIndex: 'user',
        key: 'user',
        width: '25%',
        className: 'show-default-sort',

        render(user: User) {
          return (
            <Button
              type='text'
              className='px-2 -mx-2 w-auto h-auto text-left whitespace-pre-wrap'
              onClick={() => {
                try {
                  navigator.clipboard
                    .writeText(`${user.first_name} ${user.last_name} <${user.email}>`)
                    .then(() => {
                      void message.info(`Copied to clipboard: ${user.first_name} ${user.last_name}`)
                    })
                    .catch((err: unknown) => {
                      console.error(err)
                    })
                } catch {
                  // Clipboard API not available
                }
              }}
            >
              <span>
                {user.first_name} {user.last_name} &lt;
                <span className='whitespace-nowrap'>{user.email}</span>&gt;
              </span>
            </Button>
          )
        },

        filterDropdown({ setSelectedKeys, close }) {
          return (
            <div
              className='flex flex-col items-end p-2'
              onKeyDown={event => {
                event.stopPropagation()
              }}
            >
              <Input
                ref={searchInputRef}
                placeholder='Search recipients'
                value={state.search}
                onChange={event => {
                  setSelectedKeys([event.target.value].filter(Boolean))
                  dispatch({ type: 'search/set', payload: event.target.value })
                }}
                className='block mb-2 w-full'
              />
              <Space>
                <Button
                  onClick={() => {
                    dispatch({ type: 'search/set', payload: '' })
                    close()
                  }}
                  size='small'
                >
                  Reset
                </Button>
                <Button
                  type='link'
                  size='small'
                  onClick={() => {
                    close()
                  }}
                >
                  Close
                </Button>
              </Space>
            </div>
          )
        },

        filterIcon() {
          return (
            <SearchOutlined
              className={cx({
                'text-[#1890ff]': state.search.length > 0,
                'animate-pulse': query.isLoading && state.search.length > 0
              })}
            />
          )
        },

        onFilterDropdownOpenChange(visible) {
          if (visible) {
            setTimeout(() => {
              searchInputRef.current?.select()
            }, 100)
          }
        },

        sorter: true,

        sortOrder:
          state.sort.field === 'user'
            ? state.sort.order === 'asc'
              ? 'ascend'
              : 'descend'
            : undefined,

        sortDirections: ['descend', 'ascend'],
        showSorterTooltip: true
      },
      {
        title: 'Locations & Groups',
        // dataIndex: 'locations',
        key: 'locations',
        // width: '15%',
        render(_, entry) {
          let locationsToShow = entry.user.locations.map(location => (
            <Tag key={location.id} color={locationColorMapper[location.key]} title={location.name}>
              {location.short_name}
            </Tag>
          ))

          if (locationsToShow.length > 2) {
            locationsToShow = locationsToShow.slice(0, 2)
            locationsToShow.push(
              <Tag
                key='3_locations_more_indicator'
                style={{ borderStyle: 'none', backgroundColor: 'transparent', fontSize: '20px' }}
              >
                {'\u00B7\u00B7\u00B7'}
              </Tag>
            )
          }

          let groupsToShow = entry.user.groups.map(group => (
            <Tag
              key={group.id}
              color={
                locationColorMapper[
                  locationsQuery.data?.find(l => l.id === group.location_id)?.key ?? ''
                ]
              }
              title={group.name}
            >
              {group.number}
            </Tag>
          ))

          if (groupsToShow.length > 2) {
            groupsToShow = groupsToShow.slice(0, 2)
            groupsToShow.push(
              <Tag
                key='3_groups_more_indicator'
                style={{ borderStyle: 'none', backgroundColor: 'transparent', fontSize: '20px' }}
              >
                {'\u00B7\u00B7\u00B7'}
              </Tag>
            )
          }

          return (
            <Space wrap direction='horizontal' size={[2, 6]}>
              {locationsToShow}

              {entry.user.locations.length > 0 && entry.user.groups.length > 0 && (
                <Divider type='vertical' className='mr-2 ml-0' />
              )}

              {groupsToShow}
            </Space>
          )
        }
      },
      {
        title: 'Notification Types',
        dataIndex: 'event_types',
        key: 'event_types',

        filters: Object.keys(NotificationEventType)
          // TODO: zabelin check notification-event-type.enum.ts
          .filter(key => key !== 'LuminxSingleProvider' && key !== 'ZelisSinglePaymentType')
          .map(key => NotificationEventType[key as keyof typeof NotificationEventType])
          .map(key => ({
            value: key,
            text: mapper[key as NotificationEventType]
          })),

        render(value: NotificationEventType[]) {
          return (
            <Space wrap direction='horizontal' size={[2, 6]}>
              {value.map(eventType => (
                <Tag
                  key={eventType}
                  // color={notificationColorMapper[notification]}
                >
                  {mapper[eventType]}
                </Tag>
              ))}
            </Space>
          )
        }
      },
      {
        title: 'Updated by',
        dataIndex: 'updated_by',
        key: 'updated_by',
        width: '12%',
        className: 'show-default-sort',

        render(user: User | null) {
          if (!user) {
            return <span className='text-gray-300'>—</span>
          }

          return (
            <Tooltip title={user.email}>
              <Button
                type='text'
                className='px-2 -mx-2 w-auto h-auto'
                onClick={() => {
                  try {
                    navigator.clipboard
                      .writeText(`${user.first_name} ${user.last_name} <${user.email}>`)
                      .then(() => {
                        void message.info(
                          `Copied to clipboard: ${user.first_name} ${user.last_name}`
                        )
                      })
                      .catch((e: unknown) => {
                        console.error(e)
                      })
                  } catch {
                    // Clipboard API not available
                  }
                }}
              >
                {user.first_name} {user.last_name}
              </Button>
            </Tooltip>
          )
        },

        sorter: true,

        sortOrder:
          state.sort.field === 'updated_by'
            ? state.sort.order === 'asc'
              ? 'ascend'
              : 'descend'
            : undefined,

        sortDirections: ['descend', 'ascend'],
        showSorterTooltip: true
      },
      {
        title: 'Updated at',
        dataIndex: 'updated_at',
        key: 'updated_at',
        width: '12%',
        className: 'show-default-sort',

        render(value: string) {
          if (!value) {
            return <span className='text-gray-300'>—</span>
          }

          return <span>{format(new Date(value), 'MM/dd/yyyy HH:mm')}</span>
        },

        sorter: true,

        sortOrder:
          state.sort.field === 'updated_at'
            ? state.sort.order === 'asc'
              ? 'ascend'
              : 'descend'
            : undefined,

        defaultSortOrder: 'descend',
        sortDirections: ['descend', 'ascend', 'descend'],
        showSorterTooltip: true
      },
      {
        key: 'actions',
        width: '5.5rem',
        render(_, entry) {
          const isDisabled = state.expandedRowId === entry.id

          return (
            <div className='flex justify-center'>
              <Dropdown
                disabled={isDisabled}
                menu={{
                  items: [
                    {
                      key: 'edit',
                      label: 'Edit',
                      onClick() {
                        handleExpand(true, entry)
                      },
                      icon: <EditOutlined />
                    },
                    {
                      type: 'divider'
                    },
                    {
                      key: 'delete',
                      label: 'Delete',
                      danger: true,
                      onClick() {
                        Modal.confirm({
                          title: 'Are you sure you want to delete this recipient?',
                          content: 'This action cannot be undone.',
                          okText: 'Delete',
                          okType: 'danger',
                          cancelText: 'Cancel',
                          width: 500,
                          onOk() {
                            handleDelete(entry.user.id)
                          }
                        })
                      },
                      icon: <DeleteOutlined />
                    }
                  ]
                }}
              >
                <Button
                  htmlType='button'
                  size='large'
                  type='text'
                  icon={<EllipsisOutlined />}
                  className={cx({
                    'opacity-25 cursor-not-allowed': isDisabled
                  })}
                />
              </Dropdown>
            </div>
          )
        }
      }
    ],
    [
      handleExpand,
      handleDelete,
      locationsQuery.data,
      query.isLoading,
      state.expandedRowId,
      state.search,
      state.sort.field,
      state.sort.order
    ]
  )

  const handleSave = useCallback(
    (values: FormValues) => {
      mutation.mutate({
        user_id: values.user?.value ?? '',
        event_types: values.event_types ?? []
      })
    },
    [mutation]
  )

  const handleCancel = useCallback(() => {
    dispatch({ type: 'row/collapse' })
  }, [])

  return (
    <Space direction='vertical' size={24} className='p-0 w-full'>
      {state.expandedRowId === 'new' && (
        <Card className='w-full rounded-xl' title='New Recipient'>
          <RecipientForm isNew form={form} onSave={handleSave} onCancel={handleCancel} />
        </Card>
      )}

      <Card
        className='w-full rounded-xl [&>.ant-card-body]:p-0 -mt-4'
        title='Notification Settings'
        extra={
          <Dropdown
            placement='bottomRight'
            menu={{
              items: [
                {
                  key: 'add-recipient',
                  label: 'Add Recipient',
                  onClick() {
                    resetFields()
                    dispatch({ type: 'row/expand', payload: 'new' })
                  },
                  icon: <UserAddOutlined />
                }
              ]
            }}
          >
            <Button
              htmlType='button'
              size='large'
              type='text'
              className='flex justify-center items-center -my-4'
              icon={<EllipsisOutlined />}
            />
          </Dropdown>
        }
      >
        <Table
          size='small'
          rowKey='id'
          dataSource={query.data}
          loading={debouncedLoadingStatus}
          columns={columns}
          expandable={{
            onExpand: handleExpand,

            expandedRowKeys: typeof state.expandedRowId === 'string' ? [state.expandedRowId] : [],

            expandedRowRender(entry, _index, _indent, expanded) {
              if (!expanded) {
                return null
              }

              return (
                <RecipientForm
                  isNew={false}
                  form={form}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  className='p-6'
                  notificationSetting={entry}
                />
              )
            },
            expandIcon: ExpandIcon,
            columnWidth: '2rem'
          }}
          pagination={{
            size: 'default',

            pageSizeOptions: [10, 25, 50],

            showLessItems: true,
            className: 'py-2 !pl-6 !pr-6',

            showTotal(total, range) {
              return (
                <span className='text-sm text-gray-400'>
                  Showing recipients {range[0]}–{range[1]} out of {total}
                </span>
              )
            }
          }}
          onChange={(_, filters, sorter, extra) => {
            if (extra.action === 'filter') {
              dispatch({
                type: 'filters/set',
                payload: {
                  event_types:
                    filters.event_types?.map(value => value as NotificationEventType) ?? []
                }
              })
            }

            if (extra.action === 'sort') {
              const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter

              dispatch({
                type: 'sort/set',
                payload: {
                  field: singleSorter?.column?.key as SortField | undefined,
                  order:
                    typeof singleSorter?.order === 'string'
                      ? ({ ascend: 'asc', descend: 'desc' }[singleSorter.order] as SortOrder)
                      : undefined
                }
              })
            }
          }}
        />
      </Card>
    </Space>
  )
}

interface UserFormProps {
  notificationSetting?: Entry
  isNew: boolean
  form: FormInstance<FormValues>
  className?: string
  onSave: (values: FormValues) => void
  onCancel: () => void
}

function RecipientForm({
  isNew,
  form,
  className,
  onSave,
  onCancel,
  notificationSetting
}: UserFormProps): JSX.Element {
  const [usersSearchValue, setUsersSearchValue] = useState('')
  const [debouncedUsersSearchValue, setDebouncedUsersSearchValue] = useState(usersSearchValue)

  const selectedUserOption = Form.useWatch('user', form)
  const eventTypes = Form.useWatch('event_types', form)

  useDebounce(
    () => {
      setDebouncedUsersSearchValue(usersSearchValue)
    },
    500,
    [usersSearchValue]
  )

  const recipientsQuery = useQuery<unknown, unknown, Entry[]>({
    queryKey: ['notification_settings'],

    async queryFn() {
      const response = await $api.get('/notification-recipients')

      return response.data
    },

    keepPreviousData: true,
    refetchOnWindowFocus: false
  })

  const usersQuery = useQuery<unknown, unknown, User[], ['users', string]>({
    queryKey: ['users', debouncedUsersSearchValue],

    async queryFn(context) {
      const [, search = ''] = context.queryKey

      const sp = new URLSearchParams([...(search.length > 0 ? [['search', search]] : [])])

      const { data } = await $api.get(`/users?${sp.toString()}`)

      return data.data
    },

    keepPreviousData: true
  })

  const user = isNew
    ? usersQuery.data?.find(user => user.id === selectedUserOption?.value)
    : notificationSetting?.user
  const locations = (isNew ? user?.locations : notificationSetting?.user?.locations) ?? []
  const groups = (isNew ? user?.groups : notificationSetting?.user?.groups) ?? []

  return (
    <Form
      form={form}
      size='large'
      layout='vertical'
      colon={false}
      className={className}
      onFinish={values => {
        onSave(values)
      }}
      requiredMark={false}
      labelCol={{
        className: 'font-semibold'
      }}
    >
      <Row gutter={24}>
        <Col span={14}>
          <Form.Item name='user' label='User' required>
            <Select
              placeholder='Select a user'
              labelInValue
              showSearch
              disabled={!isNew}
              filterOption={false}
              searchValue={usersSearchValue}
              options={
                usersQuery.data
                  ?.filter(
                    user => !recipientsQuery.data?.some(recipient => recipient.user.id === user.id)
                  )
                  .map(user => ({
                    value: user.id,
                    label: <UserLabel user={user} />
                  })) ?? []
              }
              onSearch={value => {
                setUsersSearchValue(value)
              }}
              onSelect={() => {
                setUsersSearchValue('')
              }}
              loading={usersQuery.isLoading}
            />
          </Form.Item>

          {isNew && selectedUserOption && !user && <Spin className='w-full' />}

          {user && locations.length === 0 && groups.length === 0 && (
            <Alert
              type='warning'
              message='This user has neither locations nor groups assigned to them'
              showIcon
            />
          )}

          {user && (locations.length > 0 || groups.length > 0) && (
            <div className='flex flex-col gap-4'>
              {locations.length > 0 && (
                <div className='flex flex-col gap-3 w-full'>
                  <span className='font-semibold'>Locations</span>

                  <Space wrap direction='horizontal' size={[2, 6]}>
                    {locations.map(location => (
                      <Tag
                        key={location.id}
                        color={locationColorMapper[location.key]}
                        title={location.name}
                      >
                        {location.short_name}
                      </Tag>
                    ))}
                  </Space>
                </div>
              )}

              {groups.length > 0 && (
                <div className='flex flex-col gap-2 w-full'>
                  <span className='font-semibold'>Groups</span>

                  <Space wrap direction='horizontal' size={[2, 6]}>
                    {groups.map(group => (
                      <Tag
                        key={group.id}
                        color={
                          locationColorMapper[
                            locations.find(location => location.id === group.location_id)?.key ?? ''
                          ]
                        }
                        title={group.name}
                      >
                        {group.number}
                      </Tag>
                    ))}
                  </Space>
                </div>
              )}
            </div>
          )}
        </Col>
        <Col span={10}>
          <Form.Item
            label='Event Types'
            shouldUpdate={(previous, current) => previous.event_types !== current.event_types}
          >
            {({ getFieldValue, setFieldValue }) => {
              const eventTypes = getFieldValue('event_types') ?? []

              const missingEventTypes = Object.keys(NotificationEventType)
                // TODO: zabelin check notification-event-type.enum.ts
                .filter(key => key !== 'LuminxSingleProvider' && key !== 'ZelisSinglePaymentType')
                .map(key => NotificationEventType[key as keyof typeof NotificationEventType])
                .filter(key => !eventTypes.includes(key))

              return (
                <>
                  {eventTypes.length > 0 && (
                    <ul className='flex flex-col gap-4 pb-4 w-full'>
                      {eventTypes.map((eventType: NotificationEventType) => (
                        <li key={eventType} className='flex gap-4 justify-start items-center'>
                          <div className='border border-[#d9d9d9] rounded-md px-[11px] py-2 flex-grow'>
                            {mapper[eventType as NotificationEventType]}
                          </div>

                          <Button
                            icon={<DeleteOutlined className='text-sm -mt-0.5' />}
                            className='flex justify-center items-center'
                            onClick={() => {
                              setFieldValue(
                                'event_types',
                                eventTypes.filter((type: Key) => type !== eventType)
                              )
                            }}
                          />
                        </li>
                      ))}
                    </ul>
                  )}

                  {missingEventTypes.length > 0 && (
                    <Dropdown
                      menu={{
                        items: [
                          ...missingEventTypes.map(key => ({
                            key,
                            label: mapper[key as NotificationEventType],
                            onClick(info: { key: unknown }) {
                              setFieldValue('event_types', [...eventTypes, info.key])
                            }
                          })),
                          ...(missingEventTypes.length > 1
                            ? [
                                {
                                  type: 'divider' as const
                                },
                                {
                                  key: 'all',
                                  label: 'All',
                                  icon: <PlusCircleOutlined className='origin-center scale-125' />,
                                  onClick() {
                                    setFieldValue('event_types', [
                                      ...eventTypes,
                                      ...missingEventTypes
                                    ])
                                  }
                                }
                              ]
                            : [])
                        ]
                      }}
                    >
                      <Button
                        type='default'
                        icon={<PlusOutlined className='text-sm -mt-0.5' />}
                        className='flex justify-center items-center'
                      >
                        Add Event Type
                      </Button>
                    </Dropdown>
                  )}
                </>
              )
            }}
          </Form.Item>
        </Col>
      </Row>

      <div className='flex justify-end'>
        <Space direction='horizontal' size={12}>
          <Button
            onClick={() => {
              onCancel()
            }}
          >
            Cancel
          </Button>

          <Button
            type='primary'
            htmlType='submit'
            disabled={(isNew && !eventTypes?.length) || !selectedUserOption}
          >
            Save
          </Button>
        </Space>
      </div>

      <Form.Item name='event_types' noStyle>
        <Input type='hidden' />
      </Form.Item>
    </Form>
  )
}

function ExpandIcon(props: RenderExpandIconProps<Entry>): JSX.Element | null {
  if (!props.expandable) {
    return null
  }

  return (
    <button
      className={cx('-ml-1 p-2 flex items-center justify-center transition-opacity translate-x-2', {
        'opacity-30 hover:opacity-75': !props.expanded,
        'opacity-100': props.expanded
      })}
      onClick={e => {
        props.onExpand(props.record, e)
      }}
    >
      {props.expanded && <CaretDownOutlined />}

      {!props.expanded && <CaretRightOutlined />}
    </button>
  )
}

interface UserLabelProps {
  user: User
}

function UserLabel({ user }: UserLabelProps): JSX.Element {
  return (
    <span>
      <span className=''>
        {user.first_name} {user.last_name}
      </span>{' '}
      <span className='text-gray-400'>{user.email}</span>
    </span>
  )
}
